/**
 * Class decorator that registers a custom element only if the tag name is
 * not already taken. Drop-in replacement for Lit's `@customElement` that
 * survives accidental double-loading of the bundle (e.g. two script tags
 * for the widget on the same page, or a cached copy beside a new one).
 *
 * customElements.define throws NotSupportedError on the second call for the
 * same tag, which would otherwise stop the second copy's script with an
 * uncaught error.
 */
export function defineOnce(tagName: string) {
  return <T extends CustomElementConstructor>(target: T): T => {
    const existing = customElements.get(tagName);
    if (!existing) {
      customElements.define(tagName, target);
    } else if (existing !== target) {
      // Surface the duplicate-bundle case so partners can find it in DevTools.
      // Silent first-wins makes stale-cache double-loads invisible to debug.
      console.warn(
        `[STEWARD] custom element "${tagName}" is already registered; ` +
        `ignoring duplicate registration. Check for two copies of the widget ` +
        `loaded on the same page (e.g. two script tags, or a cached copy beside a new one).`,
      );
    }
    return target;
  };
}
